import React from 'react';
import useStyles from './styles';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SubmissionTypeInfo } from 'types/submissions';
import { Service } from '@elevator/api-hooks/types/schema-types';
import { formatDateFromUnknown, PaymentType } from '@elevator/ui';
import OrderSummary, { OrderSummaryItem } from '../OrderSummary/OrderSummary';

export interface OrderDetailsProps {
  submissionType?: SubmissionTypeInfo | string | null;
  addOns?: Service[];
  orderNumber?: string;
  orderDate?: string | number | Date | null;
  submissionNumber?: string;
  /** Optional order total in cents for summary display. */
  total?: number | null;
  /** Optional payment type, used by OrderSummary for "Free" display rules. */
  paymentType?: PaymentType;
}

const OrderDetails = (props: OrderDetailsProps) => {
  const { classes } = useStyles();
  const {
    submissionType,
    addOns,
    orderNumber,
    orderDate,
    submissionNumber,
    total,
    paymentType,
  } = props;
  const formattedDate = formatDateFromUnknown(orderDate);
  const submissionTypeLabel =
    typeof submissionType === 'string' ? submissionType : (submissionType?.name ?? '');
  const labelVariant = 'caption' as const;
  const valueVariant = 'body1' as const;

  const orderSummaryItems: OrderSummaryItem[] = React.useMemo(() => {
    const items: OrderSummaryItem[] = [];

    if (submissionType && typeof submissionType !== 'string') {
      items.push({
        id: submissionType.serviceId,
        name: submissionType.name,
        cost: submissionType.cost ?? 0,
        isAddOn: false,
      });
    }

    if (addOns && addOns.length > 0) {
      addOns.forEach((service) => {
        items.push({
          id: service.serviceId,
          name: service.name,
          cost: service.cost ?? 0,
          isAddOn: true,
        });
      });
    }

    return items;
  }, [submissionType, addOns]);

  const orderSummaryTotal =
    typeof total === 'number'
      ? total
      : orderSummaryItems.reduce((sum, item) => sum + (item.cost ?? 0), 0);

  return (
    <Stack
      component="section"
      className={classes.root}
      direction="column"
      data-component="OrderDetails"
    >
      <Accordion disableGutters className={classes.accordion}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          className={classes.accordionSummary}
        >
          <Typography variant="h6">Order Details</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          <Stack direction="column" spacing={2}>
            <Stack direction="column" spacing={0.5}>
              <Typography variant={labelVariant} color="textSecondary">
                Order Number
              </Typography>
              <Typography variant={valueVariant}>{orderNumber ?? '—'}</Typography>
            </Stack>
            <Stack direction="column" spacing={0.5}>
              <Typography variant={labelVariant} color="textSecondary">
                Order Date
              </Typography>
              <Typography variant={valueVariant}>{formattedDate || '—'}</Typography>
            </Stack>
            <Stack direction="column" spacing={0.5}>
              <Typography variant={labelVariant} color="textSecondary">
                Submission Type
              </Typography>
              <Typography variant={valueVariant}>{submissionTypeLabel || '—'}</Typography>
            </Stack>
            <Stack direction="column" spacing={0.5}>
              <Typography variant={labelVariant} color="textSecondary">
                Submission Number
              </Typography>
              <Typography variant={valueVariant}>{submissionNumber ?? '—'}</Typography>
            </Stack>
            <OrderSummary
              title="Order Summary"
              items={orderSummaryItems}
              total={orderSummaryTotal}
              paymentType={paymentType}
              emptyMessage="No items in order."
            />
          </Stack>
        </AccordionDetails>
      </Accordion>
      <Divider variant="fullWidth" />
    </Stack>
  );
};

export default OrderDetails;
